import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { fetchWithCorpId, fetchWithCorpIdAndFormData } from '../utils/api';
import { useLocation } from 'react-router-dom';

const inputBase = { border: '1px solid #e2e8f0', borderRadius: '6px', padding: '5px 8px', fontSize: '13px', outline: 'none', width: '100%' };
const inputReadonly = { ...inputBase, background: '#f1f5f9', color: '#64748b', cursor: 'default' };
const inputEditable = { ...inputBase, background: 'white', color: '#1e293b' };
const inputDisabled = { ...inputBase, background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed' };

function TimesheetItemTable() {
  const [timesheets, setTimesheets] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timesheetItems, setTimesheetItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [highlightValidationErrors, setHighlightValidationErrors] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const timesheetId = queryParams.get('timesheetId');

  const currentTimesheet = timesheets[currentIndex];
  const isEditable = currentTimesheet?.status === 'Draft' || currentTimesheet?.status === 'Pending' || !currentTimesheet?.status;

  useEffect(() => { fetchTimesheets(); }, []);

  useEffect(() => {
    if (timesheets.length > 0 && timesheetId) {
      const index = timesheets.findIndex(ts => ts.id === parseInt(timesheetId));
      if (index !== -1) setCurrentIndex(index);
    }
  }, [timesheets, timesheetId]);

  useEffect(() => {
    if (currentTimesheet?.id) {
      fetchTimesheetItems();
      fetchAttachments();
    } else {
      setTimesheetItems([]);
      setAttachments([]);
    }
  }, [currentTimesheet?.id]);

  const fetchTimesheets = async () => {
    try {
      const employeeId = typeof window !== 'undefined' ? window.localStorage.getItem('employeeId') : null;
      const endpoint = employeeId ? `/timesheets/employee/${employeeId}` : '/timesheets';
      const data = await fetchWithCorpId(endpoint);
      setTimesheets(Array.isArray(data) ? data : []);
      if (data && data.length > 0) {
        const nonLockedIndex = data.findIndex(ts => ts && ts.status !== 'Locked');
        setCurrentIndex(nonLockedIndex !== -1 ? nonLockedIndex : 0);
      }
    } catch { setError('Failed to load timesheets');
    } finally { setLoading(false); }
  };

  const fetchTimesheetItems = async () => {
    if (!currentTimesheet?.id) return;
    setLoading(true); setError(null);
    try {
      const data = await fetchWithCorpId(`/timesheets/${currentTimesheet.id}/items`);
      setTimesheetItems(data);
    } catch (err) {
      setError('Failed to fetch timesheet items: ' + err.message);
      setTimesheetItems([]);
    }
    setLoading(false);
  };

  const fetchAttachments = async () => {
    if (!currentTimesheet?.id) return;
    try {
      const data = await fetchWithCorpId(`/timesheets/${currentTimesheet.id}/attachments`);
      setAttachments(data);
    } catch { setError('Failed to fetch attachments'); }
  };

  const handlePrevious = () => { if (currentIndex > 0) setCurrentIndex(currentIndex - 1); };
  const handleNext = () => { if (currentIndex < timesheets.length - 1) setCurrentIndex(currentIndex + 1); };

  const handleSave = async (isSubmit = false) => {
    try {
      setLoading(true); setError(null); setValidationError(null); setHighlightValidationErrors(false);

      const missingHours = timesheetItems.filter(item => {
        if (item.type === 'HOLIDAY' || item.type === 'VACATION') return false;
        return !item.hoursWorked || parseFloat(item.hoursWorked) <= 0;
      });
      if (missingHours.length > 0) {
        if (isSubmit) setHighlightValidationErrors(true);
        throw new Error('All working days must have Hours Worked greater than 0');
      }

      const missingComments = timesheetItems.filter(item => {
        if (item.type === 'HOLIDAY' || item.type === 'VACATION') return false;
        return !item.comment || item.comment.trim() === '';
      });
      if (missingComments.length > 0) {
        if (isSubmit) setHighlightValidationErrors(true);
        throw new Error('All working days must list the Activities performed on that day');
      }

      if (isSubmit) {
        const hasBillableItems = timesheetItems.some(item => item.type === 'BILLABLE');
        const requiresAttachment = hasBillableItems || currentTimesheet?.attachmentRequired;
        if (requiresAttachment && attachments.length === 0) {
          setValidationError('At least one attachment is required for this timesheet');
          return;
        }
      }

      const itemsToSubmit = timesheetItems.map(item => ({ ...item, type: item.type || 'BILLABLE' }));
      const endpoint = isSubmit ? '/submit' : '/save';
      await fetchWithCorpId(`/timesheets/${currentTimesheet.id}/items${endpoint}`, {
        method: 'PUT',
        body: JSON.stringify(itemsToSubmit)
      });

      await fetchTimesheetItems(currentTimesheet.id);
      const updatedTimesheet = await fetchWithCorpId(`/timesheets/${currentTimesheet.id}`);
      const updatedTimesheets = [...timesheets];
      updatedTimesheets[currentIndex] = updatedTimesheet;
      setTimesheets(updatedTimesheets);

      setMessage(isSubmit ? 'Timesheet submitted successfully' : 'Timesheet saved successfully');
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      setError('Failed to save timesheet items: ' + err.message);
    } finally { setLoading(false); }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...timesheetItems];
    if (field === 'type' && (value === 'HOLIDAY' || value === 'VACATION')) {
      updatedItems[index] = { ...updatedItems[index], type: value, hoursWorked: '', extraHours: '', totalHours: '', comment: '' };
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value };
    }
    if (field === 'hoursWorked' || field === 'extraHours') {
      const hoursWorked = parseFloat(updatedItems[index].hoursWorked) || 0;
      const extraHours = parseFloat(updatedItems[index].extraHours) || 0;
      updatedItems[index].totalHours = (hoursWorked + extraHours).toFixed(2);
    }
    setTimesheetItems(updatedItems);
    if (validationError) setValidationError(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentTimesheet?.id) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('timesheetId', currentTimesheet.id);
    try {
      await fetchWithCorpId(`/timesheets/${currentTimesheet.id}/attachments`, { method: 'POST', body: formData });
      fetchAttachments();
      setMessage('File uploaded successfully');
    } catch { setMessage('Failed to upload file'); }
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const corpId = localStorage.getItem('selectedCorpId') || '0';
      const response = await fetch(`${API_BASE_URL}/timesheets/${currentTimesheet.id}/attachments/${attachmentId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'X-Corp-ID': corpId, Accept: 'application/octet-stream' },
        credentials: 'include', mode: 'cors'
      });
      if (!response.ok) throw new Error('Failed to download file');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch { setMessage('Failed to download file'); }
  };

  const handleDelete = async (attachmentId) => {
    if (!currentTimesheet?.id || !isEditable) return;
    try {
      await fetchWithCorpId(`/timesheets/${currentTimesheet.id}/attachments/${attachmentId}`, { method: 'DELETE' });
      await fetchAttachments();
    } catch { setMessage('Failed to delete file'); }
  };

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (!currentTimesheet?.id) return;
    const validFiles = Array.from(e.dataTransfer.files).filter(f => {
      const t = f.type.toLowerCase();
      return t === 'application/pdf' || t === 'image/jpeg' || t === 'image/jpg' || t === 'image/png';
    });
    if (validFiles.length > 0) { setPreviewFiles(validFiles); handleUpload(validFiles); }
  };

  const handleFileSelect = (e) => {
    if (!currentTimesheet?.id) return;
    const validFiles = Array.from(e.target.files).filter(f => {
      const t = f.type.toLowerCase();
      return t === 'application/pdf' || t === 'image/jpeg' || t === 'image/jpg' || t === 'image/png';
    });
    if (validFiles.length > 0) { setPreviewFiles(validFiles); handleUpload(validFiles); }
  };

  const handleUpload = async (files) => {
    if (!currentTimesheet?.id) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('timesheetId', currentTimesheet.id);
        await fetchWithCorpIdAndFormData(`/timesheets/${currentTimesheet.id}/attachments`, formData, { method: 'POST' });
      }
      await fetchAttachments();
      setPreviewFiles([]);
      setMessage('Files uploaded successfully');
      setTimeout(() => setMessage(''), 5000);
    } catch { setMessage('Failed to upload files');
    } finally { setUploading(false); }
  };

  if (loading && timesheets.length === 0) {
    return <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Loading timesheet data…</div>;
  }

  const isNonWorking = (item) => item.type === 'HOLIDAY' || item.type === 'VACATION';
  const isErrorRow = (item) => highlightValidationErrors && !isNonWorking(item) &&
    (!item.hoursWorked || parseFloat(item.hoursWorked) <= 0 || !item.comment || item.comment.trim() === '');
  const totalHours = timesheetItems.reduce((sum, item) => sum + (parseFloat(item.totalHours) || 0), 0).toFixed(2);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="table-toolbar">
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Timesheet Details</h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
            {timesheets.length > 0 ? `${currentIndex + 1} of ${timesheets.length}` : 'No timesheets'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-secondary" onClick={handlePrevious} disabled={currentIndex === 0}>← Prev</button>
          <button className="btn-secondary" onClick={handleNext} disabled={currentIndex >= timesheets.length - 1}>Next →</button>
        </div>
      </div>

      {/* Alerts */}
      {message && <div className="alert success" style={{ marginBottom: '14px' }}>{message}</div>}
      {error && <div className="alert error" style={{ marginBottom: '14px' }}>{error}</div>}
      {validationError && <div className="alert error" style={{ marginBottom: '14px' }}>{validationError}</div>}

      {/* Timesheet Info Card */}
      {currentTimesheet && (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px 24px', marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Timesheet ID</span>
              <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>{currentTimesheet.id}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Employee</span>
              <span style={{ fontSize: '13px', color: '#1e293b' }}>{currentTimesheet.employeeName}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Vendor</span>
              <span style={{ fontSize: '13px', color: '#64748b' }}>{currentTimesheet.vendorName}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Period</span>
              <span style={{ fontSize: '13px', color: '#1e293b' }}>
                {new Date(currentTimesheet.periodStartDate + 'T00:00:00').toLocaleDateString()} – {new Date(currentTimesheet.periodEndDate + 'T00:00:00').toLocaleDateString()}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Status</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{currentTimesheet.status || 'Pending'}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: '100px' }}>Total Hours</span>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#3b82f6' }}>{currentTimesheet.totalHours}</span>
            </div>
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="table-wrapper" style={{ marginBottom: '24px' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Day</th>
              <th>Type</th>
              <th>Hours Worked</th>
              <th>Extra Hours</th>
              <th>Total Hours</th>
              <th>Activities</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(timesheetItems) && timesheetItems.length > 0 ? (<>
              {timesheetItems.map((item, index) => (
                <tr key={item.id || index} style={{ background: isErrorRow(item) ? '#fef2f2' : isNonWorking(item) ? '#f8fafc' : 'white' }}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <input type="date"
                      value={item.calendarDate ? new Date(item.calendarDate + 'T00:00:00').toISOString().split('T')[0] : ''}
                      readOnly style={inputReadonly} />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <input type="text" value={item.dayOfWeek || ''} readOnly style={{ ...inputReadonly, width: '80px' }} />
                  </td>
                  <td>
                    <select
                      value={item.type || 'BILLABLE'}
                      onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                      disabled={!isEditable}
                      style={{ ...inputBase, background: isNonWorking(item) ? '#f1f5f9' : 'white', cursor: isEditable ? 'pointer' : 'not-allowed', width: '130px' }}
                    >
                      <option value="BILLABLE">Billable</option>
                      <option value="HOLIDAY">Holiday</option>
                      <option value="VACATION">Vacation</option>
                      <option value="NON_BILLABLE">Non-Billable</option>
                    </select>
                  </td>
                  <td>
                    <input type="number" step="0.01"
                      value={item.hoursWorked || ''}
                      onChange={(e) => handleItemChange(index, 'hoursWorked', e.target.value)}
                      disabled={!isEditable || isNonWorking(item)}
                      style={{ ...((!isEditable || isNonWorking(item)) ? inputDisabled : inputEditable), width: '90px' }} />
                  </td>
                  <td>
                    <input type="number" step="0.01"
                      value={item.extraHours || ''}
                      onChange={(e) => handleItemChange(index, 'extraHours', e.target.value)}
                      disabled={!isEditable || isNonWorking(item)}
                      style={{ ...((!isEditable || isNonWorking(item)) ? inputDisabled : inputEditable), width: '90px' }} />
                  </td>
                  <td>
                    <input type="number" value={item.totalHours || '0.00'} readOnly style={{ ...inputReadonly, width: '90px' }} />
                  </td>
                  <td>
                    <input type="text"
                      value={item.comment || ''}
                      onChange={(e) => handleItemChange(index, 'comment', e.target.value)}
                      disabled={!isEditable || isNonWorking(item)}
                      style={{ ...((!isEditable || isNonWorking(item)) ? inputDisabled : inputEditable), minWidth: '320px' }}
                      placeholder="Describe activities performed…" />
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                <td colSpan="5" style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '700', fontSize: '13px', color: '#64748b' }}>Total Hours:</td>
                <td style={{ padding: '10px 14px' }}>
                  <input type="number" value={totalHours} readOnly style={{ ...inputReadonly, width: '90px', fontWeight: '700', color: '#1e293b' }} />
                </td>
                <td></td>
              </tr>
            </>) : (
              <tr className="state-row"><td colSpan="7">No timesheet items found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Attachments Section */}
      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px 24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>Attachments</h3>

        {isEditable && (
          <div
            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragActive ? '#3b82f6' : '#e2e8f0'}`,
              borderRadius: '10px',
              padding: '32px 20px',
              textAlign: 'center',
              background: dragActive ? '#eff6ff' : '#f8fafc',
              transition: 'all 0.15s',
              marginBottom: '16px',
              cursor: 'pointer'
            }}
            onClick={() => document.getElementById('file-upload').click()}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={dragActive ? '#3b82f6' : '#94a3b8'} strokeWidth="1.5" style={{ marginBottom: '10px' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#64748b' }}>
              {dragActive ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
            </p>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Accepted: PDF, PNG, JPG, JPEG</span>
            <input hidden type="file" multiple onChange={handleFileSelect} accept=".pdf,.jpg,.jpeg,.png" id="file-upload" />
          </div>
        )}

        {uploading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '13px', marginBottom: '12px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            Uploading files…
          </div>
        )}

        {attachments.length > 0 && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: '#f8fafc', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>
              Uploaded Files
            </div>
            {attachments.map((attachment) => (
              <div key={attachment.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleDownload(attachment.id, attachment.filename); }}
                  style={{ fontSize: '13px', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}
                >
                  {attachment.filename}
                </a>
                {isEditable && (
                  <button
                    onClick={() => handleDelete(attachment.id)}
                    title="Delete"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', color: '#ef4444' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Bar */}
      {isEditable && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 0' }}>
          <button className="btn-secondary" onClick={() => handleSave(false)} disabled={loading}>
            {loading ? 'Saving…' : 'Save Draft'}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={loading}
            style={{ padding: '8px 20px', border: 'none', borderRadius: '8px', background: loading ? '#94a3b8' : '#10b981', color: 'white', fontWeight: '600', fontSize: '13px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            {loading ? 'Submitting…' : 'Submit Timesheet'}
          </button>
        </div>
      )}
    </div>
  );
}

export default TimesheetItemTable;
